import { lazy, Suspense, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { MaskLines } from "./Reveal";
import { EmblemFallback, hasWebGL } from "./EmblemFallback";
import { useContent, useIntroReady } from "../lib/ContentContext";
import { prefersReduced, scrollToId } from "../lib/lenis";

const Hero3D = lazy(() => import("./Hero3D"));

export const Hero = () => {
  const { content } = useContent();
  const ready = useIntroReady();
  const ref = useRef(null);
  const [gl] = useState(() => hasWebGL());
  const [reduced] = useState(() => prefersReduced());
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const yCanvas = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const h = content.hero;
  const fadeIn = (d) => ({ initial: { opacity: 0, y: 20 }, animate: ready ? { opacity: 1, y: 0 } : {}, transition: { delay: d, duration: 0.9, ease: [0.22, 1, 0.36, 1] } });

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden flex items-end" data-testid="home-hero">
      <div className="absolute inset-0 grid-lines opacity-50 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <div className="absolute top-1/4 right-[8%] w-[560px] h-[560px] rounded-full bg-[#0066ff]/20 blur-[140px]" />
      <motion.div style={{ y: yCanvas }} className="absolute inset-x-0 top-0 h-[62svh] md:h-auto md:inset-y-0 md:left-[38%] opacity-70 md:opacity-100">
        {gl ? (
          <Suspense fallback={<EmblemFallback />}>
            <Hero3D reduced={reduced} />
          </Suspense>
        ) : (
          <EmblemFallback />
        )}
      </motion.div>

      <motion.div style={{ y: yText, opacity: fade }} className="relative z-10 w-full max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 pb-14 sm:pb-20 pt-[46svh] md:pt-40">
        <motion.p {...fadeIn(0.1)} className="eyebrow mb-6 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[#00f0ff] shadow-[0_0_12px_#00f0ff]" /> {content.company.tagline}
        </motion.p>
        <h1 className="font-display font-extrabold uppercase leading-[0.86] tracking-[-0.03em]" aria-label={`${h.line1} ${h.line2}`}>
          <MaskLines ready={ready} delay={0.15} stagger={0.14} className="block text-[clamp(3.4rem,12.5vw,11.5rem)]"
            lines={h.line1.split(" ").map((w, i) => <span key={w} className={i === 1 ? "text-gold" : "text-white"}>{w}</span>)} />
          <MaskLines ready={ready} delay={0.45} className="block mt-3 font-mono font-normal text-[clamp(0.85rem,1.6vw,1.25rem)] tracking-[0.55em] text-[#d9bf6a]" lines={[h.line2]} />
        </h1>
        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-6">
            <motion.p {...fadeIn(0.7)} className="text-2xl sm:text-3xl text-white leading-snug">
              <span className="font-serif-i text-[#f3e5ab] text-[1.15em]">{h.headline}</span>
            </motion.p>
            <motion.div {...fadeIn(0.85)} className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => scrollToId("ecosystem")} data-testid="hero-explore-ecosystem-btn"
                className="btn-gold inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 min-h-[54px] font-semibold text-sm">
                Explore Our Ecosystem <ArrowDownRight className="w-4 h-4" />
              </button>
              <Link to="/vision" data-testid="hero-discover-vision-btn"
                className="inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 min-h-[54px] text-sm border border-white/20 text-white hover:border-[#00f0ff] hover:text-[#00f0ff] transition-colors">
                Discover Our Vision <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
          <motion.div {...fadeIn(1)} className="lg:col-span-4 lg:col-start-9">
            <Link to="/founder/zahir-patel" className="group flex items-center gap-4 glass rounded-2xl p-3 pr-5 hover:border-[#d4af37]/60 transition-colors" data-testid="hero-founder-link">
              <img src="/founder/zahir-patel-portrait.webp" alt="Zahir Patel (Boss Patel), Founder & CEO" className="w-16 h-16 rounded-xl object-cover object-top" width="64" height="64" />
              <span>
                <span className="block font-mono text-[11px] tracking-[0.2em] text-[#d9bf6a]">FOUNDER & CEO</span>
                <span className="block font-display text-lg text-white mt-1">{content.founder.name} <span className="text-slate-400">({content.founder.alias})</span></span>
              </span>
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
